import React, { useState } from "react";
import { Award, CheckCircle2, XCircle, Info, Target, ChevronDown, Building2, Columns, Send, Check, FileDown } from "lucide-react";

function EstimateGrid({ e }) {
  const rows = [
    ["Est. Rate", e.rate],
    ["Points", e.points],
    ["Max Leverage", e.max_leverage],
    ["Est. Max Loan", e.max_loan],
    ["Est. DSCR", e.dscr],
    ["Term", e.term],
  ].filter(([, v]) => v);
  return (
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
      {rows.map(([k, v]) => (
        <div key={k} className="rounded-lg border border-edge bg-surface/50 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-faint">{k}</div>
          <div className="text-sm text-white font-semibold mt-0.5">{v}</div>
        </div>
      ))}
    </div>
  );
}

function ComparisonTable({ matches }) {
  const hasDscr = matches.some((m) => m.estimate.dscr);
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border border-edge" data-testid="lender-comparison-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-faint bg-surface/60 border-b border-edge">
            <th className="py-2.5 px-3 font-semibold">Lender</th>
            <th className="py-2.5 px-3 font-semibold">Rate</th>
            <th className="py-2.5 px-3 font-semibold">Points</th>
            <th className="py-2.5 px-3 font-semibold">Max Leverage</th>
            <th className="py-2.5 px-3 font-semibold">Est. Max Loan</th>
            {hasDscr && <th className="py-2.5 px-3 font-semibold">DSCR</th>}
            <th className="py-2.5 px-3 font-semibold">Term</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((m, i) => (
            <tr key={i} className={`border-b border-edge/50 ${i === 0 ? "bg-gold/[0.06]" : ""}`} data-testid={`compare-row-${m.lender_id}`}>
              <td className="py-2.5 px-3">
                <div className="text-white font-semibold flex items-center gap-1.5">{i === 0 && <Award className="w-3.5 h-3.5 text-gold" />}{m.lender_name}</div>
                <div className="text-xs text-faint">{m.product_name}</div>
              </td>
              <td className="py-2.5 px-3 text-white/85">{m.estimate.rate || "—"}</td>
              <td className="py-2.5 px-3 text-white/85">{m.estimate.points || "—"}</td>
              <td className="py-2.5 px-3 text-white/85">{m.estimate.max_leverage || "—"}</td>
              <td className="py-2.5 px-3 text-gold font-semibold">{m.estimate.max_loan || "—"}</td>
              {hasDscr && <td className="py-2.5 px-3 text-white/85">{m.estimate.dscr || "—"}</td>}
              <td className="py-2.5 px-3 text-white/70 text-xs">{m.estimate.term || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchCard({ m, best, variant, onRequest, requestedLenderId, termSheetBase }) {
  const requested = requestedLenderId && requestedLenderId === m.lender_id;
  const termHref = termSheetBase ? `${termSheetBase}?lender_id=${m.lender_id}` : null;
  return (
    <div className={`rounded-xl border p-5 ${best ? "border-gold/50 bg-gold/[0.06]" : "border-edge bg-surface/40"}`} data-testid={`lender-match-${m.lender_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {best && <span className="inline-flex items-center gap-1 rounded-full bg-gold/20 text-gold text-[11px] font-bold px-2.5 py-1 border border-gold/40"><Award className="w-3 h-3" /> BEST MATCH</span>}
            <span className="text-white font-bold text-lg">{m.lender_name}</span>
          </div>
          <div className="text-xs text-faint mt-0.5">{m.product_name}{m.lender_tagline ? ` · ${m.lender_tagline}` : ""}</div>
        </div>
        <Building2 className="w-6 h-6 text-gold/70 shrink-0" />
      </div>

      <EstimateGrid e={m.estimate} />

      {(m.reasons || []).length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {m.reasons.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-white/80"><CheckCircle2 className="w-4 h-4 text-ok mt-0.5 shrink-0" /> {r}</li>
          ))}
        </ul>
      )}

      {(m.caveats || []).length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {m.caveats.map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-warn/90"><Info className="w-4 h-4 text-warn mt-0.5 shrink-0" /> {c}</li>
          ))}
        </ul>
      )}

      {variant === "admin" && (m.estimate.fees || []).length > 0 && (
        <div className="mt-4 border-t border-edge pt-3">
          <div className="text-[11px] uppercase tracking-wide text-faint mb-1.5">Lender Fees</div>
          <ul className="space-y-1">
            {m.estimate.fees.map((f, i) => (
              <li key={i} className="flex items-center justify-between text-xs text-white/75">
                <span>{f.name} <span className="text-faint">· {f.when}</span></span>
                <span className="text-gold font-medium">{f.amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {((variant === "borrower" && onRequest) || termHref) && (
        <div className="mt-4 border-t border-edge pt-3 flex flex-wrap items-center gap-2">
          {variant === "borrower" && onRequest && (
            <button onClick={() => !requested && onRequest(m)} disabled={requested}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-semibold transition-colors ${requested ? "bg-ok/15 text-ok border border-ok/40 cursor-default" : "btn-gold"}`}
              data-testid={`request-lender-${m.lender_id}`}>
              {requested ? <><Check className="w-4 h-4" /> Requested</> : <><Send className="w-4 h-4" /> Request this lender</>}
            </button>
          )}
          {termHref && (
            <a href={termHref} target="_blank" rel="noreferrer" data-testid={`term-sheet-${m.lender_id}`}
              className="inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-semibold border border-gold/40 text-gold hover:bg-gold/10 transition-colors">
              <FileDown className="w-4 h-4" /> Term sheet
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function LenderMatch({ data, variant = "borrower", subId, requestedLenderId, onRequest, termSheetBase }) {
  const [showOthers, setShowOthers] = useState(variant === "admin");
  const [showCompare, setShowCompare] = useState(false);
  if (!data) return null;
  const { best_match, matches = [], not_matched = [], no_match_reason } = data;
  const others = matches.filter((m) => m !== best_match).length ? matches.slice(1) : [];

  return (
    <div className="mt-6 card-surface rounded-xl p-5" data-testid="lender-match">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-4 h-4 text-gold" />
        <span className="eyebrow">{variant === "admin" ? "Archer Lender Match" : "Your Best Lender Match"}</span>
      </div>

      {!best_match ? (
        <div className="mt-3 rounded-lg border border-edge bg-surface/40 p-4 text-sm text-dim" data-testid="lender-match-none">
          {no_match_reason || "We're reviewing this deal to find the right capital partner."}
        </div>
      ) : (
        <>
          <MatchCard m={best_match} best variant={variant} onRequest={onRequest} requestedLenderId={requestedLenderId} termSheetBase={termSheetBase} />

          {matches.length > 1 && (
            <div className="mt-4 flex flex-wrap items-center gap-4">
              {others.length > 0 && (
                <button onClick={() => setShowOthers((v) => !v)} className="flex items-center gap-1.5 text-sm text-gold hover:text-gold/80" data-testid="lender-match-toggle-others">
                  <ChevronDown className={`w-4 h-4 transition-transform ${showOthers ? "rotate-180" : ""}`} /> {showOthers ? "Hide" : "Show"} option cards ({others.length})
                </button>
              )}
              <button onClick={() => setShowCompare((v) => !v)} className="flex items-center gap-1.5 text-sm text-gold hover:text-gold/80" data-testid="lender-match-toggle-compare">
                <Columns className="w-4 h-4" /> {showCompare ? "Hide" : "Compare"} side-by-side ({matches.length})
              </button>
            </div>
          )}

          {showCompare && <ComparisonTable matches={matches} />}

          {showOthers && others.length > 0 && (
            <div className="mt-3 space-y-3">
              {others.map((m, i) => <MatchCard key={i} m={m} variant={variant} onRequest={onRequest} requestedLenderId={requestedLenderId} termSheetBase={termSheetBase} />)}
            </div>
          )}

          <p className="mt-4 text-xs text-faint">
            Estimates only, based on the information provided. Not an approval, commitment, or offer of financing. All terms are subject to underwriting, verification, and final approval.
          </p>
        </>
      )}

      {variant === "admin" && not_matched.length > 0 && (
        <div className="mt-5 border-t border-edge pt-4">
          <div className="text-[11px] uppercase tracking-wide text-faint mb-2">Not Matched</div>
          <div className="space-y-2">
            {not_matched.map((m, i) => (
              <div key={i} className="rounded-lg border border-brandred/30 bg-brandred/[0.06] px-3 py-2" data-testid={`lender-nomatch-${m.lender_id}`}>
                <div className="text-sm text-white/80 font-medium flex items-center gap-2"><XCircle className="w-4 h-4 text-brandred" /> {m.lender_name} · {m.product_name}</div>
                <ul className="mt-1 ml-6 space-y-0.5">
                  {m.disqualifiers.map((d, j) => <li key={j} className="text-xs text-dim list-disc">{d}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
